import { put, call, all, delay } from "redux-saga/effects";
import {
  getDataForAllUser,
  acknowledgeReceipt,
  forceStartLavTask,
  completeTask,
  getTaskByTimeline,
  adhocTaskCreateApi,
  setTourMode
} from "../../utils";
import * as actions from "../../modules/task/taskAndShiftAction";
import * as erroractions from "../../modules/error/ErrorAction";
import { commonError } from "../../modules/error/ErrorCommonFunction";

const MAX_RETRIES = 3;
const NETWORK_ABORTED = '"ECONNABORTED"';
const NETWORK_ERROR = '"Network Error"';

const isRetryableNetworkError = (error, retryCount) =>
  retryCount < MAX_RETRIES - 1 &&
  (JSON.stringify(error.code) === NETWORK_ABORTED ||
    JSON.stringify(error.message) === NETWORK_ERROR);

function* executeWithNetworkRetries(worker, onFinalError) {
  for (let retryCount = 0; retryCount < MAX_RETRIES; retryCount++) {
    try {
      yield* worker(retryCount);
      return;
    } catch (error) {
      if (isRetryableNetworkError(error, retryCount)) {
        yield delay(5000);
      } else {
        commonError(error);
        if (onFinalError) {
          yield* onFinalError();
        }
        return;
      }
    }
  }
}

function* handleAdhocTaskResponse(response) {
  if (response.data.errorResponseDTO.errorCode === 0) {
    yield all([
      yield put(
        actions.showAdhocResponseModal({
          errorCode: 200,
          errorMessage: "Lav Dump task request is succesfully submitted.",
        })
      ),
      yield put(actions.createAdhocTaskSuccess(response.data)),
    ]);
    return;
  }

  yield all([
    yield put(erroractions.showErrorAction(response.data.errorResponseDTO)),
    yield put(actions.createAdhocTaskSuccess(response.data)),
  ]);
}

const resolveBroadcastAckAction = (messageType, data) => {
  if (messageType === "broadcastAlert") {
    return actions.broadcastMessageAckSuccess(data);
  }
  if (messageType === "myTimeAlert") {
    return actions.myTimeMessageAckSuccess(data);
  }
  return actions.occurenceMessageAckSuccess(data);
};

function* callAcknowledgeReceipt(action) {
  return yield call(
    acknowledgeReceipt,
    action.userId,
    action.phoneNumber,
    action.stationCode,
    action.companyCode,
    action.taskAckList,
    action.alertAckList
  );
}

function* callWithDelayRetryOn1100(requestFn) {
  const initialResponse = yield* requestFn();
  let currentResponse = initialResponse;

  if (initialResponse.data.errorResponseDTO.errorCode === 1100) {
    yield delay(5000);
    currentResponse = yield* requestFn();
  }

  return { initialResponse, currentResponse };
}

function* postTaskAckSuccess(action, data, removeLoader = false) {
  if (removeLoader) {
    yield put(erroractions.removeLoaderAction());
  }
  yield put(
    actions.postTaskAckDataSucccess(
      data,
      action.taskAckList[0].taskRID,
      action.taskAckList[0].taskInstanceId
    )
  );
}

function* handleTaskAndShiftResponse(response) {
  if (response.data.errorResponseDTO.errorCode === 0) {
    yield put(actions.fetchTaskandShiftDataSuccess(response.data));
    return;
  }

  yield all([
    yield put(actions.fetchTaskandShiftDataSuccess(response.data)),
    yield put(erroractions.showErrorAction(response.data.errorResponseDTO)),
  ]);
}

export function* fetchTaskandShift(action) {
  yield* executeWithNetworkRetries(function* () {
      const response = yield call(
        getDataForAllUser,
        action.userId,
        action.stationCode,
        action.phoneNumber,
        action.companyCode
      );
      yield* handleTaskAndShiftResponse(response);
    });
}

export function* taskAddAcknowledge(action) {
  yield* executeWithNetworkRetries(function* () {
      yield put(erroractions.showLoaderAction());
      const { initialResponse, currentResponse } = yield* callWithDelayRetryOn1100(function* () {
        return yield* callAcknowledgeReceipt(action);
      });

      const initialCode = initialResponse.data.errorResponseDTO.errorCode;

      if (initialCode === 0) {
        yield put(actions.gsrtUpdatedStatusAck({state: initialResponse.data.gsrtUpdatePending, taskId: initialResponse.data.taskId}))
        yield* postTaskAckSuccess(action, initialResponse.data, true);
        return;
      }

      if (initialCode === 1100) {
        if (currentResponse.data.errorResponseDTO.errorCode === 0) {
          yield* postTaskAckSuccess(action, currentResponse.data);
          return;
        }
      }

      yield put(erroractions.showErrorAction(currentResponse.data.errorResponseDTO));
    });
}

export function* taskDelAcknowledge(action) {
  yield* executeWithNetworkRetries(function* () {
      yield put(erroractions.showLoaderAction());
      const { currentResponse } = yield* callWithDelayRetryOn1100(function* () {
        return yield* callAcknowledgeReceipt(action);
      });

      yield put(erroractions.removeLoaderAction());

      if (currentResponse.data.errorResponseDTO.errorCode === 0) {
        yield put(
          actions.deleteTaskAckDataSucccess(
            currentResponse.data,
            action.taskAckList[0].taskRID,
            action.taskAckList[0].taskInstanceId
          )
        );
        return;
      }

      yield put(erroractions.showErrorAction(currentResponse.data.errorResponseDTO));
    });
}

export function* forceStartLav(action) {
  yield* executeWithNetworkRetries(function* () {
      const { initialResponse, currentResponse } = yield* callWithDelayRetryOn1100(function* () {
        return yield call(
          forceStartLavTask,
          action.userId,
          action.taskInstanceId,
          action.stationCode
        );
      });

      if (currentResponse.data.errorResponseDTO.errorCode === 0) {
        if (initialResponse.data.errorResponseDTO.errorCode === 0) {
          yield put(actions.gsrtUpdatedStatusStart({state: initialResponse.data.gsrtUpdatePending, taskId: initialResponse.data.taskId}))
        }
        yield put(
          actions.forceStartLavSuccess(currentResponse.data, action.taskInstanceId)
        );
        return;
      }

      yield put(erroractions.showErrorAction(currentResponse.data.errorResponseDTO));
    });
}

export function* taskComplete(action) {
  yield* executeWithNetworkRetries(
    function* () {
      const { currentResponse } = yield* callWithDelayRetryOn1100(function* () {
        return yield call(
          completeTask,
          action.userId,
          action.phoneNumber,
          action.workArea,
          action.stationCode,
          action.companyCode,
          action.taskCompleteData
        );
      });

      if (currentResponse.data.errorResponseDTO.errorCode === 0) {
          yield put(actions.gsrtUpdatedStatusComplete({state: currentResponse.data.gsrtUpdatePending, taskId: currentResponse.data.taskId}))
          yield put(actions.completeTaskDataSucccess(currentResponse.data));
        return;
      }

      yield put(erroractions.showErrorAction(currentResponse.data.errorResponseDTO));
      yield put(actions.spinnerOnCompleteTaskError());
    },
    function* () {
      yield put(actions.spinnerOnCompleteTaskError());
    }
  );
}

export function* fetchTaskByTimeline(action) {
  yield* executeWithNetworkRetries(function* () {
      const response = yield call(
        getTaskByTimeline,
        action.userId,
        action.employeeId,
        action.phoneNumber,
        action.stationCode,
        action.companyCode,
        action.shiftId
      );

      response.data.errorResponseDTO.errorCode === 0
        ? yield put(actions.fetchTaskByTimelineSuccess(response.data))
        : yield put(
            erroractions.showErrorAction(response.data.errorResponseDTO)
          );
    });
}

export function* broadcastAck(action) {
  yield* executeWithNetworkRetries(function* () {
      const response = yield* callAcknowledgeReceipt(action);
      
      if (response.data.errorResponseDTO.errorCode === 0) {
        yield put(resolveBroadcastAckAction(action.messageType, response.data));
        return;
      }

      yield put(erroractions.showErrorAction(response.data.errorResponseDTO));
    });
}

export function* adhocTaskCreatefn(action) {
  yield* executeWithNetworkRetries(function* () {
      const response = yield call(
        adhocTaskCreateApi,
        action.userId,
        action.station,
        action.shiftId
      );

      yield* handleAdhocTaskResponse(response);
    });
}

export function* tourModeCompleted(action) {
  yield* executeWithNetworkRetries(function* () {
      const response = yield call(
        setTourMode,
        action.userId,
        action.stationCode,
        action.companyCode
      );
      
      if(response.data.errorResponseDTO.errorCode === 0) {
        yield put(actions.openTourOnLogIn(false))
      }
    });

}
